import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../../../firebase/config';
import { Pie, Line, Bar } from 'react-chartjs-2';
import moment from 'moment';
import './advanced_authlytics.css';
import { BarChart, Bar as RechartsBar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie as RechartsPie, Cell } from 'recharts';

// # Component for advanced authentication analytics
const AdvancedAuthlytics = ({ activeTab = 'sessions' }) => {
  // # State management
  const [activeSessions, setActiveSessions] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [todayLogins, setTodayLogins] = useState(0);
  const [sessionData, setSessionData] = useState({
    averageSessionLength: 0,
    deviceDistribution: {},
    sessionsByHour: Array(24).fill(0)
  });
  const [browserData, setBrowserData] = useState({
    browserDistribution: {},
    browserVersions: {},
    browserUsageByTime: {}
  });
  const [deviceData, setDeviceData] = useState({
    deviceTypes: {},
    operatingSystems: {},
    hardwareStats: {},
    geographicDistribution: {}
  });
  const [loading, setLoading] = useState(true);
  
  // # Chart type toggles
  const [deviceChartType, setDeviceChartType] = useState('bar');
  const [osChartType, setOsChartType] = useState('bar');
  const [hardwareChartType, setHardwareChartType] = useState('bar');
  const [browserDistChartType, setBrowserDistChartType] = useState('pie');
  const [browserVersionChartType, setBrowserVersionChartType] = useState('bar');

  // # Color palettes for charts
  const COLOR_PALETTES = {
    // # Modern vibrant palette
    primary: ['#3498db', '#2ecc71', '#9b59b6', '#e74c3c', '#f1c40f', '#1abc9c', '#e67e22', '#34495e'],
    // # Pastel palette
    secondary: ['#7FB3D5', '#8BC34A', '#C39BD3', '#F5B7B1', '#F9E79F', '#76D7C4', '#F5CBA7', '#85929E'],
    // # Gradient blues
    blues: ['#2980b9', '#3498db', '#5DADE2', '#85C1E9', '#AED6F1', '#D6EAF8'],
    // # Gradient greens
    greens: ['#1E8449', '#27AE60', '#58D68D', '#7DCEA0', '#A9DFBF', '#D5F5E3'],
    // # Gradient purples
    purples: ['#6C3483', '#8E44AD', '#AF7AC5', '#C39BD3', '#D7BDE2', '#EBDEF0'],
    // # Session charts
    sessions: ['#4CAF50', '#FF5252', '#2196F3', '#FFC107'],
    // # Chart.js specific colors
    chartjs: {
      blue: '#3498db',
      green: '#2ecc71',
      purple: '#9b59b6',
      red: '#e74c3c',
      yellow: '#f1c40f',
      teal: '#1abc9c',
      orange: '#e67e22',
      darkBlue: '#34495e'
    }
  };

  // # Function to parse user agent string
  const parseUserAgent = (userAgent) => {
    const browser = {
      name: 'Unknown',
      version: 'Unknown',
      os: 'Unknown',
      deviceType: 'Desktop'
    };

    // # Browser detection
    if (userAgent.includes('Firefox/')) {
      browser.name = 'Firefox';
      const match = userAgent.match(/Firefox\/([0-9.]+)/);
      if (match) browser.version = match[1];
    } else if (userAgent.includes('Chrome/')) {
      browser.name = 'Chrome';
      const match = userAgent.match(/Chrome\/([0-9.]+)/);
      if (match) browser.version = match[1];
    } else if (userAgent.includes('Safari/')) {
      browser.name = 'Safari';
      const match = userAgent.match(/Version\/([0-9.]+)/);
      if (match) browser.version = match[1];
    } else if (userAgent.includes('Edge/')) {
      browser.name = 'Edge';
      const match = userAgent.match(/Edge\/([0-9.]+)/);
      if (match) browser.version = match[1];
    }

    // # Enhanced OS detection
    const ua = userAgent.toLowerCase();
    
    // # iOS devices - check specific devices first
    if (/(iphone|ipod)/i.test(ua)) {
      browser.os = 'iOS (iPhone)';
      browser.deviceType = 'Mobile';
    } else if (/ipad/i.test(ua)) {
      browser.os = 'iOS (iPad)';
      browser.deviceType = 'Tablet';
    } else if (/mac os x/i.test(ua) && !/(iphone|ipad|ipod)/i.test(ua)) {
      // # Only set as macOS if not an iOS device
      browser.os = 'macOS';
      browser.deviceType = 'Desktop';
    } 
    // # Android detection - must come before Linux check
    else if (/android/i.test(ua)) {
      browser.os = 'Android';
      browser.deviceType = ua.includes('mobile') ? 'Mobile' : 'Tablet';
    }
    // # Linux detection - ensure it's not Android
    else if (/linux/i.test(ua) && !/android/i.test(ua)) {
      browser.os = 'Linux';
      browser.deviceType = 'Desktop';
    }
    // # Windows detection with version
    else if (userAgent.includes('Windows')) {
      if (userAgent.includes('Windows NT 10.0')) {
        browser.os = 'Windows 10/11';
      } else if (userAgent.includes('Windows NT 6.3')) {
        browser.os = 'Windows 8.1';
      } else if (userAgent.includes('Windows NT 6.2')) {
        browser.os = 'Windows 8';
      } else if (userAgent.includes('Windows NT 6.1')) {
        browser.os = 'Windows 7';
      } else {
        browser.os = 'Windows';
      }
      browser.deviceType = 'Desktop';
    }

    // # Additional device type detection
    if (!/(iphone|ipad|ipod|android)/i.test(ua)) {
      if (ua.includes('mobile')) {
        browser.deviceType = 'Mobile';
      } else if (ua.includes('tablet')) {
        browser.deviceType = 'Tablet';
      }
    }

    return browser;
  };

  // # Function to fetch session data
  const fetchSessionData = async () => {
    try {
      setLoading(true);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const currentMonthKey = `${now.getFullYear()}_${(now.getMonth() + 1).toString().padStart(2, '0')}`;
      
      // # Get users collection
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      
      // # Get activities collection
      const activitiesRef = collection(db, 'activities');
      const activitiesSnapshot = await getDocs(activitiesRef);
      
      let todayLoginCount = 0;
      let totalSessionLength = 0;
      let sessionCount = 0;
      let deviceCounts = {};
      let hourlyLogins = Array(24).fill(0);
      let totalActiveSessions = 0;

      // # Initialize browser and device tracking
      const browserStats = {
        distribution: {},
        versions: {},
        usageByTime: {}
      };

      const deviceStats = {
        types: {},
        os: {},
        hardware: {},
        geographic: {}
      };

      // # Process each activity document
      activitiesSnapshot.docs.forEach(doc => {
        const activityData = doc.data();
        const monthData = activityData[currentMonthKey];
        
        // # Count active sessions from devices map
        const devices = activityData.devices || {};
        totalActiveSessions += Object.keys(devices).length;
        
        if (monthData?.auth_history) {
          const { sign_ins = {}, sign_outs = {} } = monthData.auth_history;
          
          // # Process sign-ins
          Object.values(sign_ins).forEach(signIn => {
            const signInTime = moment(signIn.timestamp);
            const userAgentInfo = parseUserAgent(signIn.device);
            
            // # Count today's logins
            if (signInTime.isSame(moment(), 'day')) {
              todayLoginCount++;
            }
            
            // # Count hourly distribution
            hourlyLogins[signInTime.hour()]++;
            
            // # Track devices
            const deviceType = signIn.device.split(' - ')[0];
            deviceCounts[deviceType] = (deviceCounts[deviceType] || 0) + 1;

            // # Track browser stats
            browserStats.distribution[userAgentInfo.name] = (browserStats.distribution[userAgentInfo.name] || 0) + 1;
            if (!browserStats.versions[userAgentInfo.name]) {
              browserStats.versions[userAgentInfo.name] = {};
            }
            browserStats.versions[userAgentInfo.name][userAgentInfo.version] = 
              (browserStats.versions[userAgentInfo.name][userAgentInfo.version] || 0) + 1;

            // # Track device stats
            deviceStats.types[userAgentInfo.deviceType] = (deviceStats.types[userAgentInfo.deviceType] || 0) + 1;
            deviceStats.os[userAgentInfo.os] = (deviceStats.os[userAgentInfo.os] || 0) + 1;
            deviceStats.hardware[signIn.hardware_concurrency] = (deviceStats.hardware[signIn.hardware_concurrency] || 0) + 1;
            deviceStats.geographic[signIn.timezone] = (deviceStats.geographic[signIn.timezone] || 0) + 1;
            
            // # Find matching sign-out
            const matchingSignOut = Object.values(sign_outs).find(signOut => 
              moment(signOut.timestamp).isAfter(signInTime) &&
              signOut.device_id === signIn.device_id
            );
            
            if (matchingSignOut) {
              const sessionLength = calculateSessionLength(signIn, matchingSignOut);
              if (sessionLength >= 0 && sessionLength < 24 * 60) {
                totalSessionLength += sessionLength;
                sessionCount++;
              }
            }
          });
        }
      });

      // # Update state with processed data
      setActiveSessions(totalActiveSessions);
      setTotalUsers(usersSnapshot.size);
      setTodayLogins(todayLoginCount);
      setSessionData({
        averageSessionLength: sessionCount > 0 ? totalSessionLength / sessionCount : 0,
        deviceDistribution: deviceCounts,
        sessionsByHour: hourlyLogins
      });
      setBrowserData({
        browserDistribution: browserStats.distribution,
        browserVersions: browserStats.versions,
        browserUsageByTime: browserStats.usageByTime
      });
      setDeviceData({
        deviceTypes: deviceStats.types,
        operatingSystems: deviceStats.os,
        hardwareStats: deviceStats.hardware,
        geographicDistribution: deviceStats.geographic
      });
      
    } catch (error) {
      console.error('Error fetching session data:', error);
    } finally {
      setLoading(false);
    }
  };

  // # Effect to fetch data on component mount
  useEffect(() => {
    fetchSessionData();
  }, []);

  // # Function to calculate session length in minutes
  const calculateSessionLength = (signIn, signOut) => {
    const start = moment(signIn.timestamp);
    const end = moment(signOut.timestamp);
    return end.diff(start, 'minutes', true);
  };

  // # Chart configurations
  const activeSessionsChartConfig = {
    labels: ['Active Sessions', 'Inactive Users'],
    datasets: [{
      data: [activeSessions, totalUsers - activeSessions],
      backgroundColor: [COLOR_PALETTES.chartjs.green, COLOR_PALETTES.chartjs.red],
      borderWidth: 0
    }]
  };

  const todayLoginsChartConfig = {
    labels: ['Logged In Today', 'Not Logged In Today'],
    datasets: [{
      data: [todayLogins, totalUsers - todayLogins],
      backgroundColor: [COLOR_PALETTES.chartjs.blue, COLOR_PALETTES.chartjs.yellow],
      borderWidth: 0
    }]
  };

  const hourlySessionsChartConfig = {
    labels: Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`),
    datasets: [{
      label: 'Sessions by Hour',
      data: sessionData.sessionsByHour,
      borderColor: COLOR_PALETTES.chartjs.teal,
      backgroundColor: 'rgba(26, 188, 156, 0.2)',
      fill: true,
      tension: 0.4
    }]
  };

  // # Browser analytics components
  const renderBrowserAnalytics = () => {
    const browserDistributionData = Object.entries(browserData.browserDistribution)
      .map(([browser, count]) => ({
        name: browser,
        value: count
      }));

    const browserVersionsData = Object.entries(browserData.browserVersions)
      .flatMap(([browser, versions]) =>
        Object.entries(versions).map(([version, count]) => ({
          name: `${browser} ${version.split('.')[0]}`,
          value: count
        }))
      );

    return (
      <div className="advanced_authlytics_section">
        <div className="advanced_authlytics_stats-grid">
          <div className="advanced_authlytics_stat-card">
            <h3>Most Popular Browser</h3>
            <div className="advanced_authlytics_stat-value">
              {browserDistributionData.length > 0 ? 
                browserDistributionData.sort((a, b) => b.value - a.value)[0].name : 
                'N/A'}
            </div>
          </div>
          <div className="advanced_authlytics_stat-card">
            <h3>Total Browser Types</h3>
            <div className="advanced_authlytics_stat-value">
              {Object.keys(browserData.browserDistribution).length}
            </div>
          </div>
        </div>

        <div className="advanced_authlytics_charts-grid">
          <div className="advanced_authlytics_chart-container">
            <div className="advanced_authlytics_chart-header">
              <h3>Browser Distribution</h3>
              <div className="advanced_authlytics_chart-toggle">
                <button 
                  className={`advanced_authlytics_toggle-btn ${browserDistChartType === 'bar' ? 'active' : ''}`}
                  onClick={() => setBrowserDistChartType('bar')}
                >
                  Bar
                </button>
                <button 
                  className={`advanced_authlytics_toggle-btn ${browserDistChartType === 'pie' ? 'active' : ''}`}
                  onClick={() => setBrowserDistChartType('pie')}
                >
                  Pie
                </button>
              </div>
            </div>
            <div className="advanced_authlytics_chart-wrapper">
              <ResponsiveContainer width="100%" height={300}>
                {browserDistChartType === 'bar' ? (
                  <BarChart data={browserDistributionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <RechartsBar dataKey="value" fill={COLOR_PALETTES.chartjs.blue} />
                  </BarChart>
                ) : (
                  <PieChart>
                    <RechartsPie
                      data={browserDistributionData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {browserDistributionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLOR_PALETTES.primary[index % COLOR_PALETTES.primary.length]} />
                      ))}
                    </RechartsPie>
                    <Tooltip formatter={(value, name, props) => [`${value} sessions`, props.payload.name]} />
                    <Legend layout="vertical" verticalAlign="middle" align="right" />
                  </PieChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
          <div className="advanced_authlytics_chart-container">
            <div className="advanced_authlytics_chart-header">
              <h3>Browser Versions</h3>
              <div className="advanced_authlytics_chart-toggle">
                <button 
                  className={`advanced_authlytics_toggle-btn ${browserVersionChartType === 'bar' ? 'active' : ''}`}
                  onClick={() => setBrowserVersionChartType('bar')}
                >
                  Bar
                </button>
                <button 
                  className={`advanced_authlytics_toggle-btn ${browserVersionChartType === 'pie' ? 'active' : ''}`}
                  onClick={() => setBrowserVersionChartType('pie')}
                >
                  Pie
                </button>
              </div>
            </div>
            <div className="advanced_authlytics_chart-wrapper">
              <ResponsiveContainer width="100%" height={300}>
                {browserVersionChartType === 'bar' ? (
                  <BarChart data={browserVersionsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <RechartsBar dataKey="value" fill={COLOR_PALETTES.chartjs.purple} />
                  </BarChart>
                ) : (
                  <PieChart>
                    <RechartsPie
                      data={browserVersionsData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      outerRadius={100}
                      fill="#82ca9d"
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => 
                        percent > 0.05 ? `${name}: ${(percent * 100).toFixed(0)}%` : ''
                      }
                    >
                      {browserVersionsData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLOR_PALETTES.secondary[index % COLOR_PALETTES.secondary.length]} />
                      ))}
                    </RechartsPie>
                    <Tooltip formatter={(value, name, props) => [`${value} sessions`, props.payload.name]} />
                    <Legend layout="vertical" verticalAlign="middle" align="right" />
                  </PieChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // # Device analytics components
  const renderDeviceAnalytics = () => {
    const deviceTypeData = Object.entries(deviceData.deviceTypes)
      .map(([type, count]) => ({
        name: type,
        value: count
      }));

    const osData = Object.entries(deviceData.operatingSystems)
      .map(([os, count]) => ({
        name: os,
        value: count
      }));

    const hardwareData = Object.entries(deviceData.hardwareStats)
      .map(([cores, count]) => ({
        name: `${cores} Cores`,
        value: count
      }))
      .sort((a, b) => parseInt(a.name) - parseInt(b.name));

    return (
      <div className="advanced_authlytics_section">
        <div className="advanced_authlytics_stats-grid">
          <div className="advanced_authlytics_stat-card">
            <h3>Most Common Device</h3>
            <div className="advanced_authlytics_stat-value">
              {deviceTypeData.length > 0 ? 
                deviceTypeData.sort((a, b) => b.value - a.value)[0].name : 
                'N/A'}
            </div>
          </div>
          <div className="advanced_authlytics_stat-card">
            <h3>Most Used OS</h3>
            <div className="advanced_authlytics_stat-value">
              {osData.length > 0 ? 
                osData.sort((a, b) => b.value - a.value)[0].name : 
                'N/A'}
            </div>
          </div>
        </div>

        <div className="advanced_authlytics_charts-grid">
          <div className="advanced_authlytics_chart-container">
            <div className="advanced_authlytics_chart-header">
              <h3>Device Type Distribution</h3>
              <div className="advanced_authlytics_chart-toggle">
                <button 
                  className={`advanced_authlytics_toggle-btn ${deviceChartType === 'bar' ? 'active' : ''}`}
                  onClick={() => setDeviceChartType('bar')}
                >
                  Bar
                </button>
                <button 
                  className={`advanced_authlytics_toggle-btn ${deviceChartType === 'pie' ? 'active' : ''}`}
                  onClick={() => setDeviceChartType('pie')}
                >
                  Pie
                </button>
              </div>
            </div>
            <div className="advanced_authlytics_chart-wrapper">
              <ResponsiveContainer width="100%" height={300}>
                {deviceChartType === 'bar' ? (
                  <BarChart data={deviceTypeData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <RechartsBar dataKey="value" fill={COLOR_PALETTES.chartjs.teal} />
                  </BarChart>
                ) : (
                  <PieChart>
                    <RechartsPie
                      data={deviceTypeData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {deviceTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLOR_PALETTES.blues[index % COLOR_PALETTES.blues.length]} />
                      ))}
                    </RechartsPie>
                    <Tooltip formatter={(value, name, props) => [`${value} devices`, props.payload.name]} />
                    <Legend layout="vertical" verticalAlign="middle" align="right" />
                  </PieChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
          <div className="advanced_authlytics_chart-container">
            <div className="advanced_authlytics_chart-header">
              <h3>Operating System Distribution</h3>
              <div className="advanced_authlytics_chart-toggle">
                <button 
                  className={`advanced_authlytics_toggle-btn ${osChartType === 'bar' ? 'active' : ''}`}
                  onClick={() => setOsChartType('bar')}
                >
                  Bar
                </button>
                <button 
                  className={`advanced_authlytics_toggle-btn ${osChartType === 'pie' ? 'active' : ''}`}
                  onClick={() => setOsChartType('pie')}
                >
                  Pie
                </button>
              </div>
            </div>
            <div className="advanced_authlytics_chart-wrapper">
              <ResponsiveContainer width="100%" height={300}>
                {osChartType === 'bar' ? (
                  <BarChart data={osData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <RechartsBar dataKey="value" fill={COLOR_PALETTES.chartjs.green} />
                  </BarChart>
                ) : (
                  <PieChart>
                    <RechartsPie
                      data={osData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      outerRadius={100}
                      fill="#82ca9d"
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {osData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLOR_PALETTES.greens[index % COLOR_PALETTES.greens.length]} />
                      ))}
                    </RechartsPie>
                    <Tooltip formatter={(value, name, props) => [`${value} devices`, props.payload.name]} />
                    <Legend layout="vertical" verticalAlign="middle" align="right" />
                  </PieChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
          <div className="advanced_authlytics_chart-container wide">
            <div className="advanced_authlytics_chart-header">
              <h3>Hardware Distribution</h3>
              <div className="advanced_authlytics_chart-toggle">
                <button 
                  className={`advanced_authlytics_toggle-btn ${hardwareChartType === 'bar' ? 'active' : ''}`}
                  onClick={() => setHardwareChartType('bar')}
                >
                  Bar
                </button>
                <button 
                  className={`advanced_authlytics_toggle-btn ${hardwareChartType === 'pie' ? 'active' : ''}`}
                  onClick={() => setHardwareChartType('pie')}
                >
                  Pie
                </button>
              </div>
            </div>
            <div className="advanced_authlytics_chart-wrapper">
              <ResponsiveContainer width="100%" height={300}>
                {hardwareChartType === 'bar' ? (
                  <BarChart data={hardwareData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <RechartsBar dataKey="value" fill={COLOR_PALETTES.chartjs.purple} />
                  </BarChart>
                ) : (
                  <PieChart>
                    <RechartsPie
                      data={hardwareData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      outerRadius={120}
                      fill="#ffc658"
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {hardwareData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLOR_PALETTES.purples[index % COLOR_PALETTES.purples.length]} />
                      ))}
                    </RechartsPie>
                    <Tooltip formatter={(value, name, props) => [`${value} devices`, props.payload.name]} />
                    <Legend layout="vertical" verticalAlign="middle" align="right" />
                  </PieChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // # Session analytics components
  const renderSessionAnalytics = () => (
    <>
      <div className="advanced_authlytics_stats-grid">
        <div className="advanced_authlytics_stat-card">
          <h3>Active Sessions</h3>
          <div className="advanced_authlytics_stat-value">{activeSessions}</div>
          <div className="advanced_authlytics_stat-subtitle">current active sessions</div>
        </div>
        <div className="advanced_authlytics_stat-card">
          <h3>Today's Logins</h3>
          <div className="advanced_authlytics_stat-value">{todayLogins}</div>
          <div className="advanced_authlytics_stat-subtitle">users logged in today</div>
        </div>
        <div className="advanced_authlytics_stat-card">
          <h3>Average Session Length</h3>
          <div className="advanced_authlytics_stat-value">
            {Math.round(sessionData.averageSessionLength)} min
          </div>
          <div className="advanced_authlytics_stat-subtitle">per user session</div>
        </div>
        <div className="advanced_authlytics_stat-card">
          <h3>Total Users</h3>
          <div className="advanced_authlytics_stat-value">{totalUsers}</div>
          <div className="advanced_authlytics_stat-subtitle">registered users</div>
        </div>
      </div>

      <div className="advanced_authlytics_charts-grid">
        <div className="advanced_authlytics_chart-container">
          <h3>Active Sessions Distribution</h3>
          <div className="advanced_authlytics_chart-wrapper">
            <Pie 
              data={activeSessionsChartConfig}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      padding: 20,
                      font: { size: 12 }
                    }
                  }
                }
              }}
            />
          </div>
        </div>
        <div className="advanced_authlytics_chart-container">
          <h3>Today's Login Distribution</h3>
          <div className="advanced_authlytics_chart-wrapper">
            <Pie 
              data={todayLoginsChartConfig}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      padding: 20,
                      font: { size: 12 }
                    }
                  }
                }
              }}
            />
          </div>
        </div>
        <div className="advanced_authlytics_chart-container wide">
          <h3>Session Distribution by Hour</h3>
          <div className="advanced_authlytics_chart-wrapper">
            <Line 
              data={hourlySessionsChartConfig}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    grid: {
                      color: 'rgba(0, 0, 0, 0.1)'
                    }
                  },
                  x: {
                    grid: {
                      display: false
                    }
                  }
                }
              }}
            />
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="advanced_authlytics_container">
      {loading ? (
        <div className="advanced_authlytics_loading">Loading analytics data...</div>
      ) : (
        <>
          {activeTab === 'sessions' && renderSessionAnalytics()}
          {activeTab === 'browsers' && renderBrowserAnalytics()}
          {activeTab === 'devices' && renderDeviceAnalytics()}
        </>
      )}
    </div>
  );
};

export default AdvancedAuthlytics;
